import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FuturizerLogo from './FuturizerLogo';

const LOCK_KEY = 'futurizer_account_lock';

function CountdownRing({ secondsLeft, totalSecs }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const ratio = totalSecs > 0 ? secondsLeft / totalSecs : 0;
  const dashLen = circ * ratio;

  return (
    <div style={styles.ringWrap}>
      <svg width={108} height={108} viewBox="0 0 108 108" style={{ display: 'block' }}>
        <circle cx={54} cy={54} r={r} fill="none" stroke="rgba(211,47,47,0.15)" strokeWidth={7} />
        <circle
          cx={54} cy={54} r={r}
          fill="none"
          stroke="#D32F2F"
          strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={`${dashLen} ${circ}`}
          transform="rotate(-90 54 54)"
          style={{ transition: 'stroke-dasharray 0.9s linear' }}
        />
      </svg>
      <span style={styles.ringCount}>{secondsLeft}</span>
    </div>
  );
}

export default function LockedPage() {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [totalSecs,   setTotalSecs]   = useState(30);
  const [lockedEmail, setLockedEmail] = useState('');

  useEffect(() => {
    let lock;
    try { lock = JSON.parse(localStorage.getItem(LOCK_KEY)); } catch {}

    if (!lock || Date.now() >= lock.unlocksAt) {
      localStorage.removeItem(LOCK_KEY);
      navigate('/login', { replace: true });
      return;
    }

    const total = lock.totalSecs || 30;
    setLockedEmail(lock.identifier || lock.email || '');
    setTotalSecs(total);
    setSecondsLeft(Math.ceil((lock.unlocksAt - Date.now()) / 1000));

    const id = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((lock.unlocksAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        localStorage.removeItem(LOCK_KEY);
        navigate('/login', { replace: true });
      }
    }, 1000);

    return () => clearInterval(id);
  }, [navigate]);

  return (
    <div style={styles.page}>
      <div style={styles.decCircleLg} />
      <div style={styles.decCircleSm} />

      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <span style={styles.lockIcon}>🔒</span>
        </div>

        <FuturizerLogo size={36} variant="full" />

        <h1 style={styles.title}>Account Temporarily Locked</h1>

        <p style={styles.body}>
          Too many failed login attempts
          {lockedEmail && (
            <> for <strong style={{ color: 'var(--text-primary)' }}>{lockedEmail}</strong></>
          )}.
          <br />
          Please wait for the countdown to try again.
        </p>

        <CountdownRing secondsLeft={secondsLeft} totalSecs={totalSecs} />

        <p style={styles.countLabel}>
          seconds remaining
        </p>

        <div style={styles.infoBox}>
          <span style={styles.infoIcon}>ℹ️</span>
          <span style={styles.infoText}>
            You will be automatically redirected to the login page when the lockout expires.
          </span>
        </div>
      </div>

      <p style={styles.footer}>© {new Date().getFullYear()} Futurizer · Enable Development OÜ</p>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(145deg,#7F2000 0%,#BF360C 50%,#E65100 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: 'var(--font-family)',
    position: 'relative',
    overflow: 'hidden',
  },
  decCircleLg: {
    position: 'absolute', width: 500, height: 500, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(255,255,255,0.06) 0%,transparent 70%)',
    top: -150, right: -100, pointerEvents: 'none',
  },
  decCircleSm: {
    position: 'absolute', width: 280, height: 280, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(255,255,255,0.05) 0%,transparent 70%)',
    bottom: -80, left: -60, pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1,
    background: '#fff', borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-modal)',
    padding: '48px 44px 40px',
    maxWidth: 460, width: '100%',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 16,
    textAlign: 'center',
  },
  iconWrap: {
    width: 72, height: 72,
    background: 'var(--error-bg)',
    border: '2px solid var(--error)',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  lockIcon: { fontSize: 32 },
  title: {
    fontSize: 'var(--font-size-2xl)', fontWeight: 800,
    color: 'var(--error)', margin: 0, letterSpacing: '-0.4px',
  },
  body: {
    fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)',
    lineHeight: 1.65, margin: 0,
  },
  ringWrap: {
    position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginTop: 4,
  },
  ringCount: {
    position: 'absolute',
    fontSize: 28, fontWeight: 800,
    color: 'var(--error)',
    lineHeight: 1,
  },
  countLabel: {
    fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)',
    margin: '-8px 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  infoBox: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: 'var(--info-bg)', border: '1px solid rgba(21,101,192,0.18)',
    borderRadius: 'var(--radius-sm)', padding: '12px 14px',
    textAlign: 'left', width: '100%',
  },
  infoIcon: { fontSize: 16, flexShrink: 0, lineHeight: 1.4 },
  infoText: { fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', lineHeight: 1.55 },
  footer: {
    position: 'relative', zIndex: 1,
    marginTop: 28, fontSize: 'var(--font-size-xs)',
    color: 'rgba(255,255,255,0.45)',
  },
};
